/* globals Dashboard,Knitiv */

import { BaseController } from '~/utils/base-controller';
import APIV5 from '~/models/api';
import { createKTableWidget } from '~/pages/dashboard/models/widget';
import { extend } from 'lodash-es';

export default class KtableWidgetController extends BaseController {
	/** @type {Knitiv.RootScope} */
	$rootScope;

	/* @ngInject */
	/**
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {Knitiv.RootScope} $rootScope
	 */
	constructor($injector, $rootScope) {
		super($injector);

		this.shouldShowRightSection = false;

		/** @type {?Dashboard.KTableWidgetOptions} */
		this.widget = null;

		this.widgetsRight = null;

		this.$rootScope = $rootScope;

		/** @type {?Function} Désinscription du watcher posé sur $rootScope */
		this.unwatchSelected = undefined;

		/** @type {?Function} Désinscription de l'écoute de 'widget.subrequest' */
		this.offSubrequest = undefined;
	}

	/**
	 *
	 * @param {boolean} state
	 */
	highlightSelectedLine(state) {
		this.$timeout(async () => {
			const table = this.widget?.reference;

			if (table) {
				// Deselect all
				table.deselectRow();

				if (state === true) {
					const rows = table.getRows()
						.filter(r => {
							const data = r.getData();
							return data.root.kid === this.selected;
						});
					for (const row of rows) {
						row.select();
					}
				}
			}
		});
	}

	async reloadRightWidget() {
		this.error = null;
		this.widgetsRight = null;

		/** @type {import('@knitiv/api-client-javascript').QueryBuilder<'subwidget'>} */
		const subrequestBuilder = APIV5.instance.createQueryBuilder();
		subrequestBuilder
			.select(s => s
				.addColumn(c => c
					.as('subwidget')
					.withTarget('K_NODE;WIDGET'),
				),
			)
			.from(f => f
				.addElement('K_NODE;WIDGET'),
			)
			.where(w => w
				.addRule(r => r
					.equalKid('K_NODE;WIDGETACTION', this.action)
					.withPath(p => p
						.flowUp('K_NODE;WIDGETACTION'),
					),
				),
			);

		// eslint-disable-next-line unicorn/no-await-expression-member
		const lines = (await subrequestBuilder.fetchAll(50, 1, { id: 'ktable-widget.reload-right-widget' })).resolved();
		if (lines?.rows && lines?.rows.length > 0 && lines.rows[0].subwidget) {
			const subWidget = lines.rows[0].subwidget[0];

			if (subWidget.kid && subWidget.kid !== 'NONE') {
				const widgetRight = await createKTableWidget(subWidget.kid, {
					$timeout:   this.$timeout,
					$location:  this.$location,
					$injector:  this.$injector,
					$compile:   this.$compile,
					$rootScope: this.$rootScope,
				});

				if (widgetRight) {
					const widgetLink = `
							<a target="_blank" href="/query?kid=${widgetRight.queryNode.kid}">
								${widgetRight.queryNode.name}
							</a>
						`;

					const variables = widgetRight.query.variable;
					if (variables) {
						const variable = variables.find(v => v.id === 'parent');
						if (!variable) {
							this.error = this.$sce.trustAsHtml(`
									<span>Aucune variable <b>parent</b> trouvée dans la requete ${widgetLink}</span>
								`);
						}
						else if (this.widget && variable.isa === this.widget.query.from[0].node) {
							widgetRight.variables = {
								[variables[0].id]: [
									{
										kid: this.selected,
									},
								],
							};
						}
						else if (this.widget) {
							this.error = this.$sce.trustAsHtml(
								`<span>Le type de la variable <b>parent</b>
									est incompatible avec le type de la requete parente
									(${variable.isa} / ${this.widget.query.from[0].node})</span>`,
							);
						}
					}
					else {
						this.error = this.$sce.trustAsHtml(`
								<span>Aucune variables trouvée dans la requete ${widgetLink}</span>
							`);
					}

					if (widgetRight.tableOptions) {
						widgetRight.tableOptions.rowClickMenu = false;
					}

					this.$timeout(() => {
						this.widgetsRight = widgetRight;
					});
				}
			}
		}
	}

	closeSubrequest() {
		this.selected = undefined;
	}

	/**
	 * Les lignes viennent d'arriver : c'est le seul moment où la sélection peut
	 * être appliquée. `onReady` est émis sur `tableBuilt`, table encore vide, et un
	 * rafraîchissement recrée les lignes — donc perd la sélection Tabulator.
	 */
	onTableLeftDataLoaded() {
		if (this.selected) {
			this.highlightSelectedLine(true);
		}
	}

	/**
	 * Rouvre la sélection portée par l'URL. Le kid d'une ligne ne suffit pas à
	 * désigner le widget concerné — plusieurs ktables coexistent sur un tableau de
	 * bord —, d'où le `widget` déjà présent dans l'URL de cette page.
	 */
	restoreSelectionFromUrl() {
		const { selected, widget } = this.$location.search();

		if (!selected || widget !== this.widget?.kid) {
			return;
		}

		// Sans action de sous-requête, `selected` n'a aucun sens pour ce widget :
		// reloadRightWidget() interrogerait l'API avec une action indéfinie.
		if (!this.widget?.subrequestActionKid) {
			return;
		}

		this.action = this.widget.subrequestActionKid;
		this.item = selected;
		this.selected = selected;
	}

	/**
	 * Reflète la sélection dans l'URL, pour qu'un rechargement la retrouve.
	 * @param {?string} selected Ligne sélectionnée
	 * @param {?string} previous Valeur précédente
	 */
	syncSelectionInUrl(selected, previous) {
		if (selected === previous) {
			// Premier passage du watcher : l'URL fait foi, elle ne doit pas être
			// réécrite — ce serait effacer la sélection avant de l'avoir restaurée.
			return;
		}

		if (selected) {
			this.$location.search('widget', this.widget?.kid ?? null);
			this.$location.search('selected', selected);
		}
		else if (this.$location.search().widget === this.widget?.kid) {
			// Ne retirer que notre propre sélection, pas celle d'un autre widget.
			this.$location.search('selected', null);
		}
	}

	$onInit() {
		if (this.widget) {
			// this.widget.tableOptions.widget = this.widget.options;
			if (this.widget.options && 'data' in this.widget.options && this.widget.options.data?.options) {
				extend(this.widget.tableOptions, this.widget.options.data.options);
			}

			// Avant d'installer le watcher : il doit voir la sélection de l'URL comme
			// son état initial, et non comme un changement à réécrire.
			this.restoreSelectionFromUrl();

			// Posés sur $rootScope, ces deux abonnements survivent à la destruction du
			// widget : ils doivent être retirés à la main (cf. $onDestroy). Sans quoi
			// chaque visite du tableau de bord en empile un de plus, dont la closure
			// retient le contrôleur, son widget et sa table.
			this.unwatchSelected = this.$rootScope.$watch(() => this.selected, async (selected, previous) => {
				if (this.selected) {
					this.highlightSelectedLine(true);
					await this.reloadRightWidget();

					this.shouldShowRightSection = true;
				}
				else {
					this.highlightSelectedLine(false);

					this.shouldShowRightSection = false;
				}

				this.syncSelectionInUrl(selected, previous);
			});

			this.offSubrequest = this.$rootScope.$on('widget.subrequest', (
				/** @type {import('angular').IAngularEvent} */ event,
				/** @type {any} */ arguments_,
			) => {
				// if not from the same widget
				if (this.widget && arguments_.widget !== this.widget.kid) {
					return;
				}

				this.action = arguments_.action;
				this.item = arguments_.root;

				this.selected = arguments_.root;
			});
		}
	}

	$onDestroy() {
		this.unwatchSelected?.();
		this.offSubrequest?.();

		this.unwatchSelected = undefined;
		this.offSubrequest = undefined;

		// La table est détruite par kdatatable ; on relâche seulement la référence,
		// que le widget partage avec le contrôleur du tableau de bord.
		if (this.widget) {
			this.widget.reference = null;
		}

		this.widgetsRight = null;
	}
}
